import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import { FaRegBookmark, FaRegClock, FaBookmark } from 'react-icons/fa';
import Link from 'next/link';
import { IoIosArrowBack } from 'react-icons/io';
import Image from 'next/image';
import { CiSearch } from 'react-icons/ci';
import useGetUserProfileInfo from '@/hooks/auth/useGetUserProfileInfo';
import { useSavedToken } from '@/hooks/saved/useSavedToken';
import { useSearchHome } from '@/contexts/SearchHomeProvider';
import classNames from '@/lib/misc/classNames';
import { delay as delayTime } from '@/hooks/LitSessionContext/useLitAuth';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useSession } from '@/contexts/LitSessionProvider';
import { useTouchHandlerName } from '@/hooks/utilities/use-mobile';
type TokenSearchBarProps = {
  setSelectToAsset?: (_bool: boolean) => void;
  onSearch: (value: string) => void;
  placeholder?: string;
  delay?: number;
  setMode: any;
  mode?: string;
  debouncedSearchTerm?: string;
  startFocused?: boolean;
};
export const TokenSearchBar = ({
  onSearch,
  placeholder = ' Search for an asset',
  delay = 300,
  setSelectToAsset,
  setMode,
  mode,
  debouncedSearchTerm,
  startFocused = false
}: TokenSearchBarProps) => {
  const {
    PFP
  } = useGetUserProfileInfo();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const initSearchMixpanel = useRef(false);
  const {
    savedTokenData
  } = useSavedToken({});
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const {
    PKP
  } = useSession();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    searchBarisFocused,
    setSearchBarIsFocused
  } = useSearchHome();
  useEffect(() => {
    if (startFocused) {
      inputRef.current?.focus();
      setSearchBarIsFocused(true);
    }
  }, []);
  useEffect(() => {
    if (!searchTerm && searchBarisFocused) {
      setMode('recent');
    } else if (searchTerm && searchBarisFocused) {
      setMode('search');
    }
    const handler = setTimeout(() => {
      if (!initSearchMixpanel.current) {
        if (searchTerm.length < 2) {
          if (!searchTerm) {
            onSearch('');
            return;
          }
        }
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.search.searchToken,
          category: CATEGORIES.search,
          metaData: {
            search: searchTerm
          }
        });
        initSearchMixpanel.current = true;
      }
      if (!debouncedSearchTerm && mode !== 'search' && mode != 'recent') {
        setSearchTerm('');
        onSearch('');
        return;
      } else {
        onSearch(searchTerm);
      }
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, onSearch, delay]);
  useEffect(() => {
    const handleFocus = () => {
      setSearchBarIsFocused(true);
      if (!searchTerm) {
        setMode('recent');
      } else {
        setMode('search');
      }
    };
    const handleBlur = () => {
      setSearchBarIsFocused(false);
    };
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      inputElement.addEventListener('blur', handleBlur);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener('focus', handleFocus);
        inputElement.removeEventListener('blur', handleBlur);
      }
    };
  }, [setMode]);
  const path = router.asPath;
  const touchHandlerName = useTouchHandlerName();
  return <div className='w-full flex items-center text-genius-cream justify-between relative py-2 px-3 gap-2' data-sentry-component="TokenSearchBar" data-sentry-source-file="TokenSearchBar.tsx">
      <div className='!important w-[38px]  h-[34px] rounded-full bg-genius-blue flex items-center justify-center !important'>
        {/* path?.includes('mode') is for mode=search */}
        {setSelectToAsset || mode != '' || path?.includes('mode') ? <IoIosArrowBack {...{
        [touchHandlerName]: async () => {
          if (setSelectToAsset) {
            setSelectToAsset && setSelectToAsset(false);
            !setSelectToAsset && router.back();
          } else {
            if (path?.includes('mode')) {
              const newPath = path.split('?')[0];
              router.replace(newPath, newPath, {
                shallow: true
              });
            }
            await delayTime(100);
            setMode('');
            setSearchTerm('');
            //clear mode from url
          }
        }
      }} className='w-[16px]' /> : <div>
            <Link href={`/settings?previousRoute=${path}`}>
              <Image src={PFP?.includes('undefined') || PFP?.includes('null') ? '/static/genie/ava1.png' : PFP} alt='logo' height={100} width={100} className='w-[38px] h-[38px] rounded-full border-genius-blue border-[3px]' />
            </Link>
          </div>}
      </div>
      {mode !== 'recent' && <CiSearch className={classNames('w-[12px] absolute top-[20px]  text-genius-cream/50 left-[18%]')} />}
      <input ref={inputRef} type='text' value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={placeholder} className={`h-[40px] rounded-md text-[12px] bg-genius-blue w-9/12 h-6 pb-[1px] flex items-center
           px-2 font-sans font-light placeholder:text-genius-cream/50 text-genius-cream 
           focus:border-genius-pink focus:border-[1px]  pl-6`} />

      {setSelectToAsset || mode !== '' ? savedTokenData && savedTokenData.length > 0 ? <div className='w-[30px] ta-c'>
            <FaBookmark {...{
        [touchHandlerName]: () => {
          setMode('saved');
        }
      }} className='w-[15px] h-[21px] text-genius-cream cursor-pointer' />
          </div> : <div className='w-[30px] ta-c'>
            <FaRegBookmark {...{
        [touchHandlerName]: () => {
          setMode('saved');
        }
      }} className='w-[15px] h-[21px] text-genius-cream cursor-pointer' />
          </div> : <Link href='/history' aria-label='View history'>
          <FaRegClock className='w-[30px] h-[30px]' />
        </Link>}
    </div>;
};